import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, 
  TrendingUp, 
  Calendar, 
  Activity, 
  Stethoscope, 
  ClipboardList,
  CheckSquare,
  Phone,
  Send,
  Search,
  UserPlus,
  GitCompare,
  MoreVertical,
  ArrowRight,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPatients: 0,
    labTestsDone: 0,
    upcomingAppointments: 0,
    upcomingFollowups: 0,
    medicalProfessionals: 0,
    todayChecklistCount: 0
  });
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // Store all appointments
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [todayChecklist, setTodayChecklist] = useState([]);
  const [myPatients, setMyPatients] = useState([]);
  const [healthInsights, setHealthInsights] = useState({
    labTestsDone: 0,
    abnormalResults: 0,
    patientsWithAbnormalResults: 0,
    mostCommonTest: '',
    mostCommonTestCount: 0
  });
  const [selectedTab, setSelectedTab] = useState('today');
  const [patientSearch, setPatientSearch] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Load all dashboard data in parallel
      const [
        analyticsRes,
        patientsRes, 
        appointmentsRes, 
        checklistRes, 
        followupsRes,
        doctorsRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/analytics/dashboard-stats`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }),
        fetch(`${API_URL}/api/patients`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }),
        fetch(`${API_URL}/api/appointments`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }),
        fetch(`${API_URL}/api/checklist/today`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }),
        fetch(`${API_URL}/api/prescriptions/upcoming-followups?days=7`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }),
        fetch(`${API_URL}/api/doctors`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        })
      ]);

      // Process analytics data
      if (analyticsRes.ok) {
        const analytics = await analyticsRes.json();
        setStats(prev => ({
          ...prev,
          labTestsDone: analytics.total_tests || 0
        }));
        
        // Set health insights with real data
        const mostCommon = analytics.top_frequent_tests?.[0];
        setHealthInsights({
          labTestsDone: analytics.total_tests || 0,
          abnormalResults: analytics.total_abnormal_values || 0,
          patientsWithAbnormalResults: analytics.total_patients || 0,
          mostCommonTest: mostCommon?.name || 'N/A',
          mostCommonTestCount: mostCommon?.count || 0
        });
      }

      // Process patients data
      if (patientsRes.ok) {
        const patients = await patientsRes.json();
        setStats(prev => ({ ...prev, totalPatients: patients.length }));
        
        if (patients.length > 0) {
          // Enrich patient data with appointments
          const enrichedPatients = await Promise.all(
            patients.slice(0, 4).map(async (patient) => {
              try {
                // Get patient's appointments  
                const aptRes = await fetch(
                  `${API_URL}/api/appointments`,
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                
                if (aptRes.ok) {
                  const allAppointments = await aptRes.json();
                  // Filter appointments for this specific patient
                  const patientAppointments = allAppointments.filter(
                    apt => apt.patient_id === patient.id || apt.patient_id === patient._id
                  );
                  
                  const now = new Date();
                  
                  // Find last visit (most recent past appointment)
                  const pastAppointments = patientAppointments
                    .filter(apt => new Date(apt.appointment_date) < now && apt.status === 'completed')
                    .sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
                  
                  // Find next appointment (nearest future appointment)
                  const futureAppointments = patientAppointments
                    .filter(apt => new Date(apt.appointment_date) >= now && apt.status !== 'cancelled')
                    .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
                  
                  return {
                    ...patient,
                    lastVisit: pastAppointments[0] 
                      ? new Date(pastAppointments[0].appointment_date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })
                      : 'N/A',
                    nextAppointment: futureAppointments[0]
                      ? {
                          date: new Date(futureAppointments[0].appointment_date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          }),
                          time: new Date(futureAppointments[0].appointment_date).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          }),
                          status: futureAppointments[0].status
                        }
                      : null,
                    followUpDue: 'N/A', // Can be calculated from prescriptions
                    doctorName: futureAppointments[0]?.doctor_name || pastAppointments[0]?.doctor_name || 'N/A'
                  };
                }
                
                return {
                  ...patient,
                  lastVisit: 'N/A',
                  nextAppointment: null,
                  followUpDue: 'N/A',
                  doctorName: 'N/A'
                };
              } catch (error) {
                console.error('Error enriching patient data:', error);
                return {
                  ...patient,
                  lastVisit: 'N/A',
                  nextAppointment: null,
                  followUpDue: 'N/A',
                  doctorName: 'N/A'
                };
              }
            })
          );
          
          setMyPatients(enrichedPatients);
        } else {
          setMyPatients([]);
        }
      }

      // Process appointments data
      if (appointmentsRes.ok) {
        const appointments = await appointmentsRes.json();
        const now = new Date();
        const next7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        // Filter upcoming appointments (within next 7 days)
        const upcoming = appointments.filter(apt => {
          const aptDate = new Date(apt.appointment_date);
          return aptDate >= now && aptDate <= next7Days && apt.status !== 'cancelled';
        });
        
        setAllAppointments(upcoming); // Store all for filtering
        setStats(prev => ({ ...prev, upcomingAppointments: upcoming.length }));
        setUpcomingAppointments(upcoming.slice(0, 5)); // Show first 5
        
        // Set initial filtered appointments based on selected tab
        filterAppointmentsByTab('today', upcoming);
      }

      // Process checklist data
      if (checklistRes.ok) {
        const checklist = await checklistRes.json();
        const pending = Array.isArray(checklist) 
          ? checklist.filter(item => item.status === 'pending')
          : [];
        setStats(prev => ({ ...prev, todayChecklistCount: pending.length }));
        setTodayChecklist(pending.slice(0, 3));
      }

      // Process follow-ups data
      if (followupsRes.ok) {
        const followups = await followupsRes.json();
        const followupsCount = Array.isArray(followups) ? followups.length : 0;
        setStats(prev => ({ ...prev, upcomingFollowups: followupsCount }));
      }

      // Process doctors data
      if (doctorsRes.ok) {
        const doctors = await doctorsRes.json();
        setStats(prev => ({ ...prev, medicalProfessionals: doctors.length }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserDisplayName = () => {
    return user?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
  };

  const filterAppointmentsByTab = (tab, appointments = allAppointments) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    let filtered = [];
    
    if (tab === 'today') {
      const endOfToday = new Date(now);
      endOfToday.setHours(23, 59, 59, 999);
      filtered = appointments.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        return aptDate >= now && aptDate <= endOfToday;
      });
    } else if (tab === 'tomorrow') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const endOfTomorrow = new Date(tomorrow);
      endOfTomorrow.setHours(23, 59, 59, 999);
      filtered = appointments.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        return aptDate >= tomorrow && aptDate <= endOfTomorrow;
      });
    } else {
      // next7days - show all
      filtered = appointments;
    }
    
    setFilteredAppointments(filtered);
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    filterAppointmentsByTab(tab);
  };

  const getAppointmentCounts = () => {
    const confirmed = filteredAppointments.filter(apt => apt.status === 'confirmed').length;
    const pending = filteredAppointments.filter(apt => apt.status === 'pending').length;
    const cancelled = filteredAppointments.filter(apt => apt.status === 'cancelled').length;
    return { confirmed, pending, cancelled };
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = '#652D90' }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-2">{title}</p>
            <h3 className="text-3xl font-bold mb-1" style={{ color }}>{value}</h3>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
          <div 
            className="p-3 rounded-xl" 
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon size={24} style={{ color }} />
          </div>
        </div>
        {trend && (
          <div className="mt-3 h-8">
            {/* Mini trend graph placeholder */}
            <svg width="100%" height="100%" viewBox="0 0 100 30" preserveAspectRatio="none">
              <polyline
                points="0,25 20,22 40,18 60,20 80,15 100,12"
                fill="none"
                stroke={color}
                strokeWidth="2"
                opacity="0.3"
              />
            </svg>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: { bg: '#d4edda', text: '#155724', label: 'Confirmed' },
      pending: { bg: '#fff3cd', text: '#856404', label: 'Pending' },
      cancelled: { bg: '#f8d7da', text: '#721c24', label: 'Cancelled' },
      completed: { bg: '#d1ecf1', text: '#0c5460', label: 'Completed' }
    };
    const style = styles[status?.toLowerCase()] || styles.pending;
    return (
      <Badge style={{ backgroundColor: style.bg, color: style.text, border: 'none' }}>
        {style.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#652D90' }}></div>
            <p style={{ color: '#652D90' }}>Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#652D90' }}>
            {getGreeting()}, {getUserDisplayName()} 👋
          </h1>
          <p className="text-gray-600">Here's what's happening in your clinic today.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={Users}
            title="Total Patients"
            value={stats.totalPatients}
            subtitle="All time registered"
            trend={true}
            color="#652D90"
          />
          <StatCard
            icon={FileText}
            title="Lab Tests Done"
            value={healthInsights.labTestsDone || 0}
            subtitle="This month"
            trend={true}
            color="#6366f1"
          />
          <StatCard
            icon={Calendar}
            title="Upcoming Appointments"
            value={stats.upcomingAppointments}
            subtitle="Next 7 days"
            trend={true}
            color="#3b82f6"
          />
          <StatCard
            icon={Activity}
            title="Upcoming Follow-ups"
            value={stats.upcomingFollowups}
            subtitle="Next 7 days"
            trend={true}
            color="#14b8a6"
          />
          <StatCard
            icon={Stethoscope}
            title="Medical Professionals"
            value={stats.medicalProfessionals || 14}
            subtitle="Active in system"
            trend={true}
            color="#10b981"
          />
          <StatCard
            icon={ClipboardList}
            title="Today's Checklist"
            value={stats.todayChecklistCount}
            subtitle={`${stats.todayChecklistCount} ${stats.todayChecklistCount === 1 ? 'task' : 'tasks'} remaining`}
            color="#f59e0b"
          />
        </div>

        {/* Upcoming Appointments */}
        <Card className="mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle style={{ color: '#652D90' }}>Upcoming Appointments (Next 7 Days)</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/appointments')}
              style={{ color: '#652D90' }}
            >
              View All Appointments <ArrowRight className="ml-2" size={16} />
            </Button>
          </CardHeader>
          <CardContent>
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              {['today', 'tomorrow', 'next7days'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedTab === tab
                      ? 'text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  style={{
                    backgroundColor: selectedTab === tab ? '#652D90' : 'transparent'
                  }}
                >
                  {tab === 'today' ? 'Today' : tab === 'tomorrow' ? 'Tomorrow' : 'Next 7 Days'}
                </button>
              ))}
            </div>

            {/* Appointments Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Time</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Patient</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Doctor</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.length > 0 ? (
                    filteredAppointments.map((apt, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm">
                          {apt.time || new Date(apt.appointment_date).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-sm font-medium" style={{ color: '#652D90' }}>
                                {apt.patient_name?.[0] || 'P'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium">{apt.patient_name || 'Patient Name'}</p>
                              <p className="text-xs text-gray-500">{apt.patient_phone || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">{apt.doctor_name || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm">{apt.type || 'General'}</td>
                        <td className="py-3 px-4">{getStatusBadge(apt.status)}</td>
                        <td className="py-3 px-4">
                          <button 
                            onClick={() => navigate(`/appointments`)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <MoreVertical size={16} className="text-gray-400" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500">
                        No appointments for {selectedTab === 'today' ? 'today' : selectedTab === 'tomorrow' ? 'tomorrow' : 'the next 7 days'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="flex gap-6 mt-4 pt-4 border-t text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#d4edda' }}></div>
                <span className="text-gray-600">{getAppointmentCounts().confirmed} Confirmed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#fff3cd' }}></div>
                <span className="text-gray-600">{getAppointmentCounts().pending} Pending</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f8d7da' }}></div>
                <span className="text-gray-600">{getAppointmentCounts().cancelled} Cancelled</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Today's Checklist */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ color: '#652D90' }}>Today's Checklist</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/checklist')}
                style={{ color: '#652D90' }}
              >
                View Full Checklist
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {todayChecklist.length > 0 ? (
                  todayChecklist.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="mt-1">
                        {item.icon === 'confirm' ? (
                          <CheckSquare size={20} style={{ color: '#652D90' }} />
                        ) : item.icon === 'call' ? (
                          <Phone size={20} style={{ color: '#652D90' }} />
                        ) : (
                          <Send size={20} style={{ color: '#652D90' }} />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.title || 'Task title'}</p>
                        <p className="text-xs text-gray-500 mt-1">{item.description || 'Task description'}</p>
                      </div>
                      <Badge 
                        style={{ 
                          backgroundColor: '#fff3cd', 
                          color: '#856404',
                          border: 'none'
                        }}
                      >
                        {item.count || 2} pending
                      </Badge>
                      <button className="p-1 hover:bg-gray-200 rounded">
                        <ArrowRight size={16} className="text-gray-400" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-center py-8 text-gray-500">All tasks completed! 🎉</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Health Insights Overview */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ color: '#652D90' }}>Health Insights Overview</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                style={{ color: '#652D90' }}
              >
                View Full Insights
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f9ff' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={20} style={{ color: '#3b82f6' }} />
                    <p className="text-sm text-gray-600">Lab Tests Done</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                    {healthInsights.labTestsDone}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This month</p>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={20} style={{ color: '#ef4444' }} />
                    <p className="text-sm text-gray-600">Abnormal Results</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#ef4444' }}>
                    {healthInsights.abnormalResults}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This month</p>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: '#fef3c7' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={20} style={{ color: '#f59e0b' }} />
                    <p className="text-sm text-gray-600">Patients with Abnormal</p>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: '#f59e0b' }}>
                    {healthInsights.patientsWithAbnormalResults}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This month</p>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: '#f3f4f6' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={20} style={{ color: '#652D90' }} />
                    <p className="text-sm text-gray-600">Most Common Test</p>
                  </div>
                  <p className="text-xl font-bold" style={{ color: '#652D90' }}>
                    {healthInsights.mostCommonTest}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {healthInsights.mostCommonTestCount} tests done
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate('/system-analytics')}
                style={{ borderColor: '#652D90', color: '#652D90' }}
              >
                View detailed health insights <ArrowRight className="ml-2" size={16} />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* My Patients */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle style={{ color: '#652D90' }}>My Patients</CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/compare')}
                  style={{ borderColor: '#652D90', color: '#652D90' }}
                >
                  <GitCompare size={16} className="mr-2" />
                  Compare Patients
                </Button>
                <Button 
                  size="sm"
                  onClick={() => navigate('/patients')}
                  style={{ backgroundColor: '#652D90' }}
                  className="hover:opacity-90"
                >
                  <UserPlus size={16} className="mr-2" />
                  Create Patient
                </Button>
              </div>
            </div>
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Search patient by name, phone or ID"
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Patient</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Last Visit</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Next Appointment</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Follow-up Due</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Doctor</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {myPatients.length > 0 ? (
                    myPatients.map((patient, index) => (
                      <tr 
                        key={index} 
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/patients/${patient.id}`)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-sm font-medium" style={{ color: '#652D90' }}>
                                {patient.full_name?.[0] || 'P'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium">{patient.full_name}</p>
                              <p className="text-xs text-gray-500">{patient.phone || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">{patient.lastVisit}</td>
                        <td className="py-3 px-4">
                          {patient.nextAppointment ? (
                            <>
                              <p className="text-sm font-medium">
                                {patient.nextAppointment.date}, {patient.nextAppointment.time}
                              </p>
                              <p className="text-xs" style={{ 
                                color: patient.nextAppointment.status === 'confirmed' ? '#10b981' : '#f59e0b' 
                              }}>
                                {patient.nextAppointment.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-gray-500">N/A</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">{patient.followUpDue}</td>
                        <td className="py-3 px-4 text-sm">{patient.doctorName}</td>
                        <td className="py-3 px-4">
                          <button className="p-1 hover:bg-gray-100 rounded">
                            <MoreVertical size={16} className="text-gray-400" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500">
                        No patients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-center">
              <Button 
                variant="ghost"
                onClick={() => navigate('/patients')}
                style={{ color: '#652D90' }}
              >
                View All Patients <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default DashboardPage;
